import { supabase } from '../lib/supabase';

/**
 * ⚠️ SECURITY NOTICE: This service has been refactored to remove direct OpenAI API calls
 * 
 * WHY THIS CHANGE WAS MADE:
 * - Previous implementation used `dangerouslyAllowBrowser: true` with VITE_OPENAI_API_KEY
 * - VITE_ prefix exposes environment variables to browser JavaScript (PUBLIC)
 * - This caused API key leaks visible in browser DevTools and compiled code
 * 
 * CURRENT ARCHITECTURE (Secure):
 * - All AI functionality uses Supabase Edge Functions OR backend API (server-side)
 * - Use growGuardianAIService.js for secure AI operations
 * - OpenAI API keys stored ONLY on backend server
 * 
 * MIGRATION COMPLETE:
 * - Chat assistant now uses growGuardianAIService.chatAssistant()
 * - Demo mode disabled (requires authentication for security)
 * - All AI requests route through https://growguardian.work/api/ai/*
 */

export const aiChatService = {
  // Get user's conversations
  async getConversations(userId) {
    try {
      const { data, error } = await supabase?.from('ai_chat_conversations')?.select('*')?.eq('user_id', userId)?.eq('conversation_status', 'active')?.order('last_message_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  },

  // Create new conversation
  async createConversation(userId, title = 'IPM Guidance Chat') {
    try {
      const { data, error } = await supabase?.from('ai_chat_conversations')?.insert({
          user_id: userId,
          title: title,
          conversation_status: 'active'
        })?.select()?.single();
      
      if (error) throw error;

      // Add welcome message
      await this.addSystemMessage(data?.id, userId, 
        'Welcome to GrowGuardian AI! I am your IPM expert assistant trained on comprehensive agricultural pest management knowledge. I can help you with pest identification, treatment recommendations, biological controls, and chemical applications. How can I help you today?'
      );

      return data;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  },

  // Get messages for a conversation
  async getMessages(conversationId, userId) {
    try {
      const { data, error } = await supabase?.from('ai_chat_messages')?.select(`*,referenced_module:learning_modules(id, title, chapter_number),referenced_product:products(id, name, category, use_case)`)?.eq('conversation_id', conversationId)?.eq('user_id', userId)?.order('created_at', { ascending: true });
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  },

  // Add user message
  async addUserMessage(conversationId, userId, content, messageType = 'text', metadata = {}) {
    try {
      const { data, error } = await supabase?.from('ai_chat_messages')?.insert({
          conversation_id: conversationId,
          user_id: userId,
          role: 'user',
          message_type: messageType,
          content: content,
          metadata: metadata
        })?.select()?.single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding user message:', error);
      throw error;
    }
  },

  // Add system message
  async addSystemMessage(conversationId, userId, content, metadata = {}) {
    try {
      const { data, error } = await supabase?.from('ai_chat_messages')?.insert({
          conversation_id: conversationId,
          user_id: userId,
          role: 'system',message_type: 'system_notification',
          content: content,
          metadata: { ...metadata, system_message: true }
        })?.select()?.single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding system message:', error);
      throw error;
    }
  },

  /**
   * @deprecated Use growGuardianAIService.chatAssistant() instead
   * This method has been removed to prevent API key leaks
   * ALL AI generation now routes through secure backend API
   */
  async generateAiResponse() {
    throw new Error('Direct AI generation removed for security. Use growGuardianAIService.chatAssistant() instead');
  },

  /**
   * @deprecated Use growGuardianAIService.chatAssistant() instead
   * This method has been removed to prevent API key leaks
   * ALL AI streaming now routes through secure backend API
   */
  async generateAiResponseStream() {
    throw new Error('Direct AI streaming removed for security. Use growGuardianAIService.chatAssistant() instead');
  },

  /**
   * @deprecated Demo mode removed - Authentication required for security
   * Users must sign in to access AI chat features
   */
  async generateDemoResponse() {
    throw new Error('Demo mode disabled. Please sign in to use AI chat features. All AI requests require authentication and route through secure backend API.');
  },

  // Get suggestions for conversation
  async getSuggestions(conversationId) {
    try {
      const { data, error } = await supabase?.from('ai_chat_suggestions')?.select('*')?.eq('conversation_id', conversationId)?.eq('is_active', true)?.order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      return [];
    }
  },

  // Toggle bookmark for message
  async toggleBookmark(messageId, userId) {
    try {
      const { data: message, error: fetchError } = await supabase?.from('ai_chat_messages')?.select('is_bookmarked')?.eq('id', messageId)?.eq('user_id', userId)?.single();
      
      if (fetchError) throw fetchError;
      
      const { data, error } = await supabase?.from('ai_chat_messages')?.update({ is_bookmarked: !message?.is_bookmarked })?.eq('id', messageId)?.eq('user_id', userId)?.select()?.single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      throw error;
    }
  },

  // Get bookmarked messages
  async getBookmarkedMessages(userId) {
    try {
      const { data, error } = await supabase?.from('ai_chat_messages')?.select(`*,conversation:ai_chat_conversations(id, title),referenced_module:learning_modules(id, title, chapter_number),referenced_product:products(id, name, category)`)?.eq('user_id', userId)?.eq('is_bookmarked', true)?.order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching bookmarked messages:', error);
      throw error;
    }
  },

  // Archive conversation
  async archiveConversation(conversationId, userId) {
    try {
      const { data, error } = await supabase?.from('ai_chat_conversations')?.update({ conversation_status: 'archived' })?.eq('id', conversationId)?.eq('user_id', userId)?.select()?.single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error archiving conversation:', error);
      throw error;
    }
  },

  // Update conversation title
  async updateConversationTitle(conversationId, userId, newTitle) {
    try {
      const { data, error } = await supabase?.from('ai_chat_conversations')?.update({ title: newTitle })?.eq('id', conversationId)?.eq('user_id', userId)?.select()?.single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating conversation title:', error);
      throw error;
    }
  }
};